<template>
  <div class="mt-2 add-to-cart-block">
    <router-link
      :to="product.brandUrl"
      v-text="product.brandName"
      class="brand-name"
    />
    <h1 class="product-name" v-text="product.name" />
    <div
      v-if="product.isDiscounted"
      class="discounted-price my-2"
    >
      {{ product.oldPrice }}
      <span
        v-if="product.oldPriceSecondaryFormatted !== null"
        class="ml-1"
      >
        ({{ product.oldPriceSecondaryFormatted }})
      </span>
    </div>

    <div
      v-if="product.loginPriceFormatted !== null"
    >
      <div class="login-price">
        {{ $t('term_torzsar_login') }}:
      </div>
      <div class="price my-2 login-price">
        {{ product.loginPriceFormatted }}
        <span
          v-if="product.loginPriceSecondaryFormatted !== null"
          class="ml-1"
        >
          ({{ product.loginPriceSecondaryFormatted }})
        </span>
        <b-icon-info-circle-fill
          class="info-icon cursor-pointer"
          @click="showInfoModal"
        />
      </div>
      <div
        v-if="product.loginUnitPriceFormatted !== null && product.loginUnitPriceFormatted !== false"
        class="unit-price my-2 login-price"
      >
        {{ product.loginUnitPriceFormatted }}
        <template v-if="product.loginUnitPriceSecondaryFormatted !== null">
          ({{ product.loginUnitPriceSecondaryFormatted }})
        </template>
      </div>
      <div v-if="product.discountPercent > 1" class="discount-value-container mb-2">
        <span class="font-weight-bold">{{ $t('term_megtakaritas') }}:</span>
        <span>
          {{ product.discountValueFormatted }}
          <span
            v-if="product.discountValueInSecondaryFormatted !== null"
            class="ml-1"
          >
            ({{ product.discountValueInSecondaryFormatted }})
          </span>
        </span>
        <span class="discount-percent-badge">
          {{ product.discountPercent }}%
        </span>
      </div>
      <router-link v-t="'term_login_or_register_for_price'" to="/login" />
    </div>
    <div v-else-if="product.discountPercent > 1" class="discount-value-container mb-2">
      <span class="font-weight-bold">{{ $t('term_megtakaritas') }}:</span>
      <span>
        {{ product.discountValueFormatted }}
        <span
          v-if="product.discountValueInSecondaryFormatted !== null"
          class="ml-1"
        >
          ({{ product.discountValueInSecondaryFormatted }})
        </span>
      </span>
      <span class="discount-percent-badge">
        {{ product.discountPercent }}%
      </span>
    </div>

    <div
      v-text="currentPriceFormatted"
      class="price my-2"
      :class="{
        'discounted': product.isDiscounted,
        'discounted-price': product.loginPriceFormatted !== null
      }"
    />
    <div
      v-if="product.hasSpecialShippingFlag"
      class="spec-shipping-price my-2"
    >
      <div
        v-t="'term_specialis_szallitasi_ar'"
        class="label"
      />
      <div class="price">
        +{{ product.specialShippingPriceFormatted }}
      </div>
    </div>
    <div
      v-if="product.unitPriceFormatted !== null"
      class="unit-price my-2"
    >
      {{ product.unitPriceFormatted }}
      <template v-if="product.unitPriceSecondaryFormatted !== null">
        ({{ product.unitPriceSecondaryFormatted }})
      </template>
    </div>

    <div v-if="product.point > 0" class="my-2 product-point">
      +{{ product.point }} {{ $t('term_pont') }}
    </div>

    <div v-if="product.isItemPackage">
      <product-package
        v-for="(productPackage, index) in product.packageComponentProductList"
        :key="productPackage.id + '_' + index"
        :unique-id="productPackage.id + '_' + index"
        :product-package="productPackage"
        class="my-2"
        @updateProductPackageItem="updateProductPackageItem"
      />
      <quantity-input
        :quantity="quantity"
        class="mb-3"
        @quantityIncreased="quantityIncreased"
        @quantityDecreased="quantityDecreased"
      />
    </div>
    <div v-else-if="product.isOrderable">
      <div
        v-if="product.stockInfo !== null"
        :class="product.stockInfo.inStock ? 'text-success' : 'text-danger'"
        class="my-2"
        v-html="product.stockInfo.msg"
      />
      <custom-select
        v-if="dosenPriceList.length > 0"
        :select-options="dosenPriceList"
        :value="selectedQuantityOption"
        :title-name="$t('tucat_ar')"
        @input="quantitySelected"
      />
      <product-param-variant-selects
        v-if="product.paramVariantList !== null"
        :param-variant-list="product.paramVariantList"
        :default-color="selectedColor"
        :default-flavor="selectedFlavor"
        :default-size="selectedSize"
        @flavorSelected="flavorSelected"
        @sizeSelected="sizeSelected"
        @colorSelected="colorSelected"
        @isValidChanged="isValidChanged"
      />
      <quantity-input
        :quantity="quantity"
        class="mb-3"
        @quantityIncreased="quantityIncreased"
        @quantityDecreased="quantityDecreased"
      />
      <div
        v-if="product.hasAssemblyFlag"
      >
        <div class="custom-control custom-checkbox ml-4">
          <input
            :value="isAssemblyChecked"
            type="checkbox"
            class="custom-control-input"
            id="assembly-checkbox"
            @input="$emit('assemblyChanged', !isAssemblyChecked)"
          >
          <label class="custom-control-label assembly-label" for="assembly-checkbox">
            <div v-t="'term_osszeszerles_kerem'"/>
            <div>
              (+{{ product.assemblyPriceFormatted }})
            </div>
          </label>
        </div>
      </div>
      <div
        v-if="isCartBtnDisabled"
        v-html="$t('nincs_ilyen_valaszthato_parameter')"
        class="text-danger my-2"
      />
    </div>
    <div v-if="product.giftProductList.length > 0">
      <gift-product
        v-for="(giftProduct, index) in product.giftProductList"
        :key="giftProduct.id + '_' + index"
        :gift-product="giftProduct"
        class="my-2"
        @updateGiftProduct="updateGiftProduct"
      />
    </div>
    <button
      v-if="!isCatalogPage"
      v-t="'term_megrendelem'"
      class="custom-button w-100"
      :disabled="!product.isOrderable || isCartBtnDisabled || isProductAddToCartInProgress"
      @click="addToCart"
      data-cy="add-to-cart-button"
    />
    <button
      v-if="isLoggedIn"
      v-t="'general_favorite'"
      class="custom-button w-100 unfilled mt-3 product-favorite"
      :disabled="isFavoriteToggleInProgress"
      :class="{
        'favorite': isFavoriteItem,
      }"
      @click="toggleFavorite"
    />
    <div v-if="!product.isAdsFreeProduct && product.otherPackagingItemList.length > 0">
      <div class="mt-3">
        <span class="other-packaging" v-t="'termek_mas_kiszerelesei'" />
      </div>
      <div
        class="other-packaging-container position-relative"
      >
        <product-card
          v-for="(otherPackageProduct, index) in otherPackageList"
          :key="otherPackageProduct.id + '_' + index"
          :product="otherPackageProduct"
          :is-cart-item="false"
          :is-product-add-to-cart-in-progress="isProductAddToCartInProgress"
          class="my-2"
          :class="{
            'overlay position-relative pr-1 pb-1': product.otherPackagingItemList.length > 3 && otherPackageProductCountToShow === 3 && index === 2
          }"
          @addToCart="$emit('productCardAddToCart', otherPackageProduct)"
        />
        <div
          v-if="product.otherPackagingItemList.length > 3 && otherPackageProductCountToShow === 3"
          class="show-all-other-packaging-product w-100 d-flex justify-content-center position-absolute"
        >
          <button
            aria-label="Több termék betöltése"
            class="custom-button unfilled"
            @click="otherPackageProductCountToShow = product.otherPackagingItemList.length"
          />
        </div>
      </div>
    </div>
    <info-modal
      v-if="isInfoModalOpen"
      @close="closeInfoModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { NOTY_TYPE } from '../../constant/builderConsts.js'
import { BIconInfoCircleFill } from 'bootstrap-vue'

export default {
  name: 'productAddToCart',
  components: {
    BIconInfoCircleFill,
    'custom-select': () => import(/* webpackChunkName: "customSelect" */ '../customSelect/customSelect.vue'),
    'quantity-input': () => import(/* webpackChunkName: "quantityInput" */ '../quantityInput/quantityInput.vue'),
    'product-card': () => import(/* webpackChunkName: "productCard" */ '../productCard/productCard.vue'),
    'product-package': () => import(/* webpackChunkName: "productPackage" */ '../productPackage/productPackage.vue'),
    'product-param-variant-selects': () => import(/* webpackChunkName: "productParamVariantSelects" */ '../productParamVariantSelects/productParamVariantSelects.vue'),
    'gift-product': () => import(/* webpackChunkName: "giftProduct" */ '../giftProduct/giftProduct.vue'),
    'info-modal': () => import(/* webpackChunkName: "InfoModal" */ '../infoModal/info-modal.vue'),
  },
  props: {
    /**
     * termék object
     *
     * @type {{
     *   brandName: String,
     *   brandUrl: String,
     *   compositionList: [
     *     {
     *       calorie: String,
     *       calorieFat: String,
     *       tableDataList: [
     *         carb: {
     *           dv: String,
     *           value: String,
     *         },
     *         carbNet: {
     *           dv: String,
     *           value: String,
     *         },
     *         colesterin: {
     *           dv: String,
     *           value: String,
     *         },
     *         fat: {
     *           dv: String,
     *           value: String,
     *         },
     *         fiber: {
     *           dv: String,
     *           value: String,
     *         },
     *         fullFat: {
     *           dv: String,
     *           value: String,
     *         },
     *         fullFatMultiple: {
     *           dv: String,
     *           value: String,
     *         },
     *         fullFatSingle: {
     *           dv: String,
     *           value: String,
     *         },
     *         kalium: {
     *           dv: String,
     *           value: String,
     *         },
     *         natrium: {
     *           dv: String,
     *           value: String,
     *         },
     *         protein: {
     *           dv: String,
     *           value: String,
     *         },
     *         sugar: {
     *           dv: String,
     *           value: String,
     *         },
     *         sugarAlcohol: {
     *           dv: String,
     *           value: String,
     *         },
     *         transFat: {
     *           dv: String,
     *           value: String,
     *         },
     *       ],
     *       comment: String,
     *       description: String,
     *       ingredientList: [
     *         {
     *           amount: String,
     *           name: String,
     *           rda: String,
     *         },
     *       ],
     *       isCalorieCheck: Boolean,
     *       name: String,
     *       nameWithoutBrand: String,
     *       serving: String,
     *       servingSize: String,
     *     },
     *   ],
     *   description: String,
     *   id: Number,
     *   imageList: [
     *     String,
     *   ],
     *   isDiscounted: Boolean,
     *   isOrderable: Boolean,
     *   discountValueFormatted: String,
     *   discountPercent: Number,
     *   isVisible: Boolean,
     *   name: String,
     *   oldPrice: String,
     *   similarItemList: Array,
     *   relevantArticleList: Array,
     *   replaceData: Array,
     *   paramVariantList: {
     *     flavors: [
     *       {
     *         code: String,
     *         isDefault: Boolean,
     *         label: String,
     *         selectableList: [
     *           color: [
     *             String,
     *           ],
     *           flavor: [
     *             String,
     *           ],
     *           size: [
     *             String,
     *           ],
     *         ]
     *       },
     *     ],
     *     colors: [
     *       {
     *         code: String,
     *         isDefault: Boolean,
     *         label: String,
     *         selectableList: [
     *           color: [
     *             String,
     *           ],
     *           flavor: [
     *             String,
     *           ],
     *           size: [
     *             String,
     *           ],
     *         ]
     *       },
     *     ],
     *     sizes: [
     *       {
     *         code: String,
     *         isDefault: Boolean,
     *         label: String,
     *         selectableList: [
     *           color: [
     *             String,
     *           ],
     *           flavor: [
     *             String,
     *           ],
     *           size: [
     *             String,
     *           ],
     *         ]
     *       },
     *     ],
     *   }|null,
     *   point: Number,
     *   price: String,
     *   questionsUrl: String,
     *   rateValue: Number,
     *   reviewBarData: [{
     *     starCount: Number,
     *     percent: Number,
     *   },],
     *   reviewCount: Number,
     *   reviewsUrl: String,
     *   compositionsUrl: String,
     *   selectedColor: String|null,
     *   selectedFlavor: String|null,
     *   selectedSize: String|null,
     *   unitPriceFormatted: String|null,
     *   loginPriceFormatted: String|null,
     *   loginUnitPriceFormatted: String|null,
     *   hasSpecialShippingFlag: Boolean,
     *   specialShippingPriceFormatted: String|null,
     *   isPriceBySizeType: Boolean,
     *   isPriceDosenType: Boolean,
     *   isItemPackage: Boolean,
     *   packageComponentProductList: Array,
     *   otherPackagingItemList: Array,
     *   giftProductList: Array,
     *   dataLayerItemData: Array,
     *   dosenPrices: [
     *     {
     *       quantity: Number,
     *       priceFormatted: String,
     *     },
     *   ],
     *   hasAssemblyFlag: Boolean,
     *   isAdsFreeProduct: Boolean,
     *   assemblyPriceFormatted: String|null,
     *   stockInfo: {
     *     inStock: Number,
     *     msg: String,
     *   }|null,
     * }}
     */
    product: {
      type: Object,
      required: true,
    },
    /**
     * kosárhoz adás gomb le van-e tiltva
     */
    isOtherComponentSelectsValid: {
      type: Boolean,
      default: true,
    },
    /**
     * kosárba adás folyamatban van-e, ha igen disabled a gomb
     *
     * @type {boolean}
     */
    isProductAddToCartInProgress: {
      type: Boolean,
      default: false,
    },
    /**
     * tucat árak és mennyiségek lista
     */
    dosenPriceList: {
      type: Array,
      default: () => [],
    },
    /**
     * termék mennyisége
     */
    quantity: {
      type: Number,
      required: true,
    },
    /**
     * termékhez választott íz
     */
    selectedFlavor: {
      type: String|null,
      required: true,
    },
    /**
     * termékhez választott szín
     */
    selectedColor: {
      type: String|null,
      required: true,
    },
    /**
     * termékhez választott méret
     */
    selectedSize: {
      type: String|null,
      required: true,
    },
    /**
     * termékhez választott méret
     */
    currentPriceFormatted: {
      type: String,
      required: true,
    },
    /**
     * termék szerelés kérése be van-e pipálva
     */
    isAssemblyChecked: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      /**
       * termék saját param selectjei validak-e
       *
       * @type {Boolean}
       */
      isProductSelectsValid: true,
      /**
       * jelenleg ennyit mutatunk max a termék más kiszereléseinek listán
       *
       * @type {Number}
       */
      otherPackageProductCountToShow: 3,
      /**
       * info modal felnyitása
       */
      isInfoModalOpen: false,
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'User/isLoggedIn',
      favoritesList: 'User/getFavorites',
      isFavoriteToggleInProgress: 'User/getIsFavoriteAddOrRemoveInProgress',
      isCatalogPage: 'Site/getIsCatalogPage',
    }),
    /**
     * további kiszerelések item lista, ha nincs lenyitva akkor az első párat közülük, egyébként mindet
     */
    otherPackageList() {
      return this.product.otherPackagingItemList.length > 3
        ? this.product.otherPackagingItemList.slice(0, this.otherPackageProductCountToShow)
        : this.product.otherPackagingItemList
    },
    /**
     * le van e tiltve a kosárhoz adás gomb
     *
     * @return {boolean}
     */
    isCartBtnDisabled() {
      return !this.isProductSelectsValid || !this.isOtherComponentSelectsValid
    },
    /**
     * adott cikk a kedvencek között van-e
     *
     * @return {boolean}
     */
    isFavoriteItem() {
      if (!this.isLoggedIn) {
        return false
      }
      const favoriteItemIndex = this.favoritesList.favoritesShopItems.findIndex(favoriteItem => parseInt(favoriteItem.ID) === parseInt(this.product.id))
      return favoriteItemIndex !== -1
    },
    /**
     * tucat áras terméknél a jelenleg érvényben lévő tucat mennyiség
     */
    selectedQuantityOption() {
      if (this.dosenPriceList.length === 0) {
        return null
      }
      return this.dosenPriceList.filter(dosenPrice => dosenPrice.quantity <= this.quantity)
        .reduce((dosenPriceFirst, dosenPriceSecond) => {
          return dosenPriceFirst.quantity > dosenPriceSecond.quantity ? dosenPriceFirst : dosenPriceSecond
        }).code
    },
  },
  methods: {
    ...mapActions({
      addToFavorites: 'User/addToFavorites',
      removeFavorite: 'User/removeFavorite',
    }),
    /**
     * felnyitjuk az info modalt
     */
    showInfoModal() {
      this.isInfoModalOpen = true
    },
    /**
     * bezárjuk a modalt
     */
    closeInfoModal() {
      this.isInfoModalOpen = false
    },
    /**
     * csomag item küldése
     */
    updateProductPackageItem(productPackageItem) {
      this.$emit('updateProductPackageItem', productPackageItem)
    },
    /**
     * ajándéktermék küldése
     */
    updateGiftProduct(giftProduct) {
      this.$emit('updateGiftProduct', giftProduct)
    },
    /**
     * termékhez választott paramok valid-e
     */
    isValidChanged(newIsValid) {
      this.isProductSelectsValid = newIsValid
      this.$emit('isValidChanged', this.isProductSelectsValid)
    },
    /**
     * tucat mennyiség kiválasztva
     *
     * @param value
     */
    quantitySelected(value) {
      this.$emit('quantitySelected', value)
    },
    /**
     * iz változtatása
     *
     * @param value
     */
    flavorSelected(value) {
      this.$emit('flavorSelected', value)
    },
    /**
     * szin változtatása
     *
     * @param value
     */
    colorSelected(value) {
      this.$emit('colorSelected', value)
    },
    /**
     * meret változtatása
     *
     * @param value
     */
    sizeSelected(value) {
      this.$emit('sizeSelected', value)
    },
    /**
     * mennyiség növelése
     */
    quantityIncreased() {
      this.$emit('quantityIncreased')
    },
    /**
     * mennyiség csökkentése
     */
    quantityDecreased() {
      this.$emit('quantityDecreased')
    },
    /**
     * termék kosárhoz adása
     */
    addToCart() {
      this.$emit('addToCart')
    },
    /**
     * cikk kedvencekhez adás/kivétel
     */
    toggleFavorite() {
      if (this.isFavoriteToggleInProgress) {
        return
      }
      if (this.isFavoriteItem) {
        const favoriteItemIndex = this.favoritesList.favoritesShopItems.findIndex(favoriteItem => parseInt(favoriteItem.ID) === parseInt(this.product.id))
        this.removeFavorite({
          product_id: this.product.id,
          bookmarkId: this.favoritesList.favoritesShopItems[favoriteItemIndex].BID,
          type: 'shop_item',
        })
          .then(() => {
            this.$root.$emit('notificationPush', this.$t('term_kedvencek_torolve'), NOTY_TYPE.NOTY_SUCCESS)
          })
      } else {
        this.addToFavorites({
          product_id: this.product.id,
          type: 'shop_item',
        })
          .then(() => {
            this.$root.$emit('notificationPush', this.$t('term_kedvencek_koze'), NOTY_TYPE.NOTY_SUCCESS)
          })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@media screen and (min-width: 991px) {
  .add-to-cart-block {
    min-width: 374px;
  }
}

.brand-name {
  text-transform: uppercase;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: var(--product-add-to-card-secondary-color);

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

.other-packaging,
.unit-price {
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: var(--product-add-to-card-secondary-color);

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

.price {
  font-size: 18px;
  line-height: 24px;
  font-weight: 800;
  color: #111;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }

  &.discounted {
    color: #d6001c;
  }
}

.login-price {
  color: #d6001c;
}

.discounted-price {
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: var(--product-add-to-card-secondary-color);
  text-decoration: line-through;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

.product-name {
  color: #111;
  font-weight: 800;
  font-size: 28px;
  line-height: 40px;

  @media screen and (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
}

.spec-shipping-price {
  .label {
    color: #4f4f4f;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;

    @media screen and (max-width: 768px) {
      font-size: 12px;
    }
  }

  .price {
    font-weight: 800;
    font-size: 18px;
    line-height: 24px;

    @media screen and (max-width: 768px) {
      font-size: 16px;
    }
  }
}

.assembly-label {
  color: #111;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

.discount-value-container {
  font-size: 12px;

  .discount-percent-badge {
    background-color: #d7331a;
    padding: 4px 8px;
    border-radius: var(--product-table-discount-border-radius);
    color: #fff;
  }
}

.other-packaging-container {
  .show-all-other-packaging-product {
    bottom: 0;
    z-index: 1;

    button {
      background-image: url("../../assets/svg/arrow_down.svg");
      background-size: 100%;
      background-repeat: no-repeat;
      background-position: center;
      padding: 15px;
    }
  }

  .overlay:after {
      content: "";
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #ffffff 75%);
      height: 100%;
      left: 0;
      overflow: hidden;
      position: absolute;
      bottom: 0;
      width: 100%;
  }
}

.info-icon{
  color: #0f98c9
}
</style>
